/**
 * OrderingGuidelinesBanner — sticky promo-style bar at the top of My Events.
 *
 * One-line summary + "View guidelines" (full guidelines in a dialog) + a
 * dismiss X persisted to localStorage so it stays gone across visits.
 */
import { useState } from "react";
import { BookOpenCheck, Package, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const DISMISS_KEY = "h365-ordering-guidelines-dismissed";

const GUIDELINES: { title: string; body: string }[] = [
  {
    title: "Start from the Catalog",
    body: "Every event begins in the Catalog — name your event, pick a date, and add the products you want designed. Our design team takes it from there.",
  },
  {
    title: "Add products early",
    body: "You can add more products to an event until design work begins (while it's still “Submitted Design Request”). After that, start a new event for additional items.",
  },
  {
    title: "Designs come in versions",
    body: "Designers deliver an initial design (V1); requested revisions arrive as V2, V3, and so on. You'll choose exactly which version of each product to move forward with.",
  },
  {
    title: "Quotes unlock when designs are finished",
    body: "Once a design is finished (and licensing is cleared where needed), you can request a quote for the versions you pick. Our team prices it and you'll see it under My Quotes.",
  },
  {
    title: "Timelines",
    body: "Habits Exclusives take ~30–35 days to make; Basics ship expedited in ~5–8 days. Plan your event date with production time in mind.",
  },
];

export const OrderingGuidelinesBanner = () => {
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(DISMISS_KEY) === "1",
  );
  const [open, setOpen] = useState(false);

  if (dismissed) return null;

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
  };

  return (
    /**
     * Rendered by ClientShell ABOVE the scrollable page content (pinned to the
     * shell's top container, full content width — no sticky/margin hacks).
     */
    <div className="shrink-0 border-b border-primary/20 bg-primary/5 px-3 sm:px-4 md:px-6">
      <div className="flex items-center gap-3 py-2.5">
        <Package className="h-4 w-4 shrink-0 text-primary" />
        <p className="min-w-0 flex-1 truncate text-sm">
          <span className="font-semibold">Ordering Guidelines</span>
          <span className="hidden text-muted-foreground sm:inline">
            {" "}
            — how events, designs, versions, and quotes work.
          </span>
        </p>
        <Button
          variant="outline"
          size="sm"
          className="h-7 shrink-0 gap-1.5 border-primary/30 text-xs text-primary hover:text-primary"
          onClick={() => setOpen(true)}
        >
          <BookOpenCheck className="h-3.5 w-3.5" />
          View guidelines
        </Button>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss ordering guidelines"
          className="grid h-6 w-6 shrink-0 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5 text-primary" />
              Ordering Guidelines
            </DialogTitle>
            <DialogDescription>
              How events, designs, versions, and quotes work — start to finish.
            </DialogDescription>
          </DialogHeader>
          <ol className="space-y-4">
            {GUIDELINES.map((g, i) => (
              <li key={g.title} className="flex gap-3">
                <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                  {i + 1}
                </span>
                <div className="min-w-0 space-y-0.5">
                  <p className="text-sm font-semibold">{g.title}</p>
                  <p className="text-sm leading-relaxed text-muted-foreground">{g.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrderingGuidelinesBanner;
